import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { UploadService } from 'src/app/services/core/documents/upload.service';
import { FundingSourceService } from 'src/app/services/core/funding-source/funding-source.service';
import { validateFileSize } from 'src/app/utils/common.utils';
import { addressValidator } from 'src/app/utils/custom-validators';
import { imageProcessing, ImageSnippet } from 'src/app/utils/image-processing-utils';
import { sweetAlert } from 'src/app/utils/swal';
import { urls } from 'src/app/utils/url.utils';
import { allowOnlyNumbersValidator, phoneNumberLengthValidator, timeZone } from 'src/app/utils/utils.common';
@UntilDestroy()

@Component({
  selector: 'app-add-funding-source',
  templateUrl: './add-funding-source.component.html',
  styleUrls: ['./add-funding-source.component.css']
})
export class AddFundingSourceComponent implements OnInit {
  // Edit Modal Inputs and Outputs
  @Input() fundingSource:any;
  @Input() source:any;
  @Output() onSaveFundingSource = new EventEmitter<string>();
  @Output() onCloseModal = new EventEmitter<string>();
  // Edit Modal Inputs and Outputs End

  submitted = false;
  fundingSourceForm!: FormGroup;
  fundingSourceId: any;
  profileImageURL: any;
  selectedFile: ImageSnippet | null = null;
  timezones:any = moment.tz.names();
  titleCase = new TitleCasePipe();
  fundingSourceDetails:any

  constructor(
    private fundingSourceService: FundingSourceService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private uploadService: UploadService,
  ) { }

  ngOnInit() {
    if (this.source === 'modal' && this.fundingSource) {
      this.fundingSourceId = this.fundingSource.id;
      this.getFundingSourceById();
    }
    
    this.fundingSourceForm = this.fb.group({
      title: ['', Validators.required],
      contactNumber: ['', [Validators.required, phoneNumberLengthValidator]],
      phoneNumber: ['', [phoneNumberLengthValidator]],
      email: ['', [Validators.required, Validators.email]],
      address: this.fb.group({
        name: ['', Validators.required],
        coords: [[]],
        city: ['',Validators.required],
        state: ['', Validators.required],
        zipCode: ['',Validators.required],
        aptSuiteRoom: [''],
      }, { validators: addressValidator() }),
      profileImageURL: ['',Validators.required],
      isActive: [true, Validators.required],
      accountStatus: ['normal', Validators.required],
      timeZone: [timeZone, Validators.required],
      isDefault: [false],
      googleClientId: [''],
      appleClientId: [''],
      playStoreUrl: [''],
      appStoreUrl: [''],
      portalUrl: [''],
    }, {
      validators: [
        allowOnlyNumbersValidator('contactNumber'),
        allowOnlyNumbersValidator('phoneNumber'),
      ]
    }
  );
  }

  onSelectAddress(place: any): void {
    const selectedAddress = place.address_components;
    const city = selectedAddress.find((data: any) =>
      data.types.includes('locality') || data.types.includes('administrative_area_level_2')
    );
    const cityName = city ? city.long_name : '';
    const state = selectedAddress.find((data: any) =>
      data.types.includes('administrative_area_level_1')
    );
    const stateName = state ? state.long_name : '';
     const zipCode = selectedAddress.find((data: any) =>
      data.types.includes('postal_code')
    );
    const zip = zipCode ? zipCode.long_name : '';
    this.fundingSourceForm.patchValue({
      address: {
        city: cityName,
        state: stateName,
        name: place.formatted_address,
        zipCode: zip,
        coords: [
          place.geometry.location.lng(),
          place.geometry.location.lat(),
        ]
      }
    });
  }

  onSave() {
    this.submitted = true;
    if (this.fundingSourceForm.invalid) {
      return;
    }
    let payload = this.fundingSourceForm.value;
    this.fundingSourceService.save(this.fundingSourceId, payload).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          if (this.source === 'modal') {
            this.onSaveFundingSource.emit(data);
          } else {
            this.router.navigateByUrl('/funding-sources');
          }
        }
      },
      error: (err) => {
        sweetAlert("Error", err, "error", "OK");
      }
    });
  }

  getFundingSourceById() {
    this.fundingSourceService
      .getById(this.fundingSourceId)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.fundingSourceDetails = data;
          this.profileImageURL = data.profileImageURL;
          this.fundingSourceForm.patchValue({
            ...data,
            title: this.titleCase.transform(data.title)
          });
        }
      })
  }

  processFile(imageInput: any) {
    const file = imageInput.files?.[0];
   if (!validateFileSize(file)) {
         return;
       }
    imageProcessing(imageInput).pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.selectedFile = result.file;
        if (!this.selectedFile || !(this.selectedFile instanceof Blob)) {
          console.error('Invalid file selected.');
          return;
        }
        this.uploadService.uploadImageToServer(this.selectedFile, urls.FUNDING_SOURCES).pipe(untilDestroyed(this)).subscribe({
          next: (uploadResult: any) => {
            if (uploadResult?.fileUrl) {
              this.fundingSourceForm.get('profileImageURL')?.setValue(uploadResult.fileUrl);
              this.profileImageURL = uploadResult.fileUrl
              // this.profileImageURL = uploadResult.fileUrl; 
            }
          },
          error: (uploadError) => {
            console.error('Error uploading image:', uploadError);
          },
        });
      },
      error: (processingError) => {
        console.error('Error processing image:', processingError);
      },
    });
  }

  copyIdToClipBoard() {
    navigator.clipboard.writeText(this.fundingSourceId)
    .then(() => {
      console.log("Copied!");
    })
    .catch(err => {
      console.error("Could not copy", err);
    });
  }

  get form() {
    return this.fundingSourceForm.controls;
  }

  get addressForm() {
    return this.fundingSourceForm.controls.address as FormGroup;
  }


  onChangeAddress() {
    this.fundingSourceForm.patchValue({
      address: {
        coords: '',
        city: '',
        state: '',
        zipCode: '',
      }
    });
  }

 async onChangeToggle(event: Event) {
  const isChecked = (event.target as HTMLInputElement).checked;

  const isActive = this.fundingSourceForm.get('isActive')?.value;

  // 🚨 BLOCK: cannot set default if inactive
  if (isChecked && !isActive) {
    await sweetAlert(
      "Alert",
      "Only active funding sources can be set as default.",
      "warning",
      "OK"
    );

    this.fundingSourceForm.patchValue({
      isDefault: false,
    });

    return;
  }

  if (!isChecked) {
    this.fundingSourceForm.patchValue({
      isDefault: false,
    });
    return;
  }

  // existing logic
  if (this.fundingSourceDetails?.isDefault) return;

  const result = await sweetAlert(
    "Alert",
    "Are you sure you want to set this as default?",
    "warning",
    "Yes",
    "No"
  );

  if (result.value) {
    this.fundingSourceForm.patchValue({ isDefault: true });
  } else {
    this.fundingSourceForm.patchValue({ isDefault: false });
  }
}

async onStatusChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value === 'true';

  const isDefault = this.fundingSourceForm.get('isDefault')?.value;

  // 🚨 If making inactive but already default → block
  if (!value && isDefault) {
    await sweetAlert(
      "Alert",
      "Set a new default funding source before marking this one as inactive.",
      "warning",
      "OK"
    );

    this.fundingSourceForm.get('isActive')?.setValue(true, { emitEvent: false });
    return;
  }

  // If becoming inactive → auto remove default (extra safety)
  if (!value) {
    this.fundingSourceForm.patchValue({
      isDefault: false
    });
  }

  this.fundingSourceForm.get('isActive')?.setValue(value, { emitEvent: false });
}

}
